import dayjs from "dayjs";
import { useInfiniteQuery } from "react-query";
import { supabase } from "../../../config/supabaseClient";
import { useMemo, useCallback } from "react";
import { StaticVariables } from "../../StaticVariables";
import { reactQueryParams } from "../../StayUpToDate/RenderComponent";

const POSTS_PER_PAGE = 40;

export function useLoadYesterdayContent(
  username: any,
  user_id: any,
  daysNumber: any,
  postIds: any,
  removedPostIds: any,
  name: any,
  isChange?: boolean,
  contentOrder?: any,
  customOrderPostIds?: any,
  queryScope: string = "default"
) {
  // Memoize processed removedPostIds to avoid recalculating on every render
  const processedRemovedPostIds = useMemo(() => {
    if (!removedPostIds || removedPostIds === "") return [];
    return Array.from(
      new Set(removedPostIds.split(",").filter((elem: any) => elem !== ""))
    );
  }, [removedPostIds]);

  // Memoize content platforms to avoid recreating on every call
  const contentPlatforms = useMemo(() => StaticVariables().CONTENT_PLATFORMS, []);

  const getPaginatedFeed = useCallback(async ({ pageParam = 1 } = {}) => {
    // For multiple date ranges, fetch enough data to cover all pages up to current page
    // This accounts for deduplication across date ranges
    // Fetch 2x the needed items to account for potential duplicates
    const neededItems = pageParam * POSTS_PER_PAGE;
    const fetchLimit = Math.max(neededItems * 2, POSTS_PER_PAGE * 2);
    
    // Parallel fetch all date ranges at once instead of sequentially
    const fetchPromises = daysNumber.map((num: number) => {
      let numDayStart = "";
      let numDayEnd = "";
      
      if (num === 30) {
        numDayStart = dayjs().utc()
          .subtract(1, "month")
          .format("YYYY-MM-DD HH:mm:ss");
        numDayEnd = dayjs().utc().format("YYYY-MM-DD HH:mm:ss");
      } else {
        numDayStart = dayjs().utc()
          .subtract(num, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss");
        numDayEnd = num === 7
          ? dayjs().utc().format("YYYY-MM-DD HH:mm:ss")
          : dayjs().utc()
              .subtract(num, "day")
              .endOf("day")
              .format("YYYY-MM-DD HH:mm:ss");
      }

      return supabase.rpc("get_yesterday_content", {
        userid: user_id,
        user_name: username,
        start_date: numDayStart,
        end_date: numDayEnd,
        post_ids: [],
        order_type: contentOrder,
        content_platforms: contentPlatforms,
        custom_order: customOrderPostIds || [],
        limit_num: fetchLimit,
        offset_num: 0, // Always fetch from start, we'll paginate after combining
        // NEW PARAMETER - all filtering now happens in SQL
        removed_post_ids: processedRemovedPostIds.length > 0 ? processedRemovedPostIds : [],
      });
    });

    // Wait for all queries to complete in parallel
    const results = await Promise.all(fetchPromises);

    // Check for errors
    const error = results.find((result) => result.error)?.error;
    if (error) {
      throw new Error(error.message);
    }

    // Extract data, total, and post_ids from RPC response
    // RPC should return: { data: Array, total: number, post_ids: Array }
    const allResults = results.map((result) => {
      const responseData = result.data?.data || result.data || [];
      const totalCount = result.data?.total || 0;
      const allPostIds = result.data?.post_ids || [];
      return { responseData, totalCount, allPostIds };
    });

    // Flatten all data from all date ranges
    const allData = allResults.flatMap((r) => r.responseData);
    
    // Remove duplicates by post_id (keep first occurrence)
    // Note: Removed posts are already filtered in SQL, so we only need to deduplicate
    const uniqueData = Array.from(
      new Map(allData.map((item: any) => [item.post_id, item])).values()
    );

    // Track original data length from RPC response (after SQL filtering, before deduplication)
    const originalDataLength = uniqueData.length;

    // Calculate total count from all unique posts (after deduplication)
    // This represents the total unique posts across all date ranges after SQL filtering
    const totalCount = uniqueData.length;

    // Apply pagination to the combined and deduplicated data
    const startIndex = (pageParam - 1) * POSTS_PER_PAGE;
    const endIndex = startIndex + POSTS_PER_PAGE;
    const paginatedData = uniqueData.slice(startIndex, endIndex);

    // Return paginated structure
    return {
      data: paginatedData,
      total_count: totalCount, // Total count of unique posts after deduplication
      originalDataLength: paginatedData.length, // Length of current page data
      post_ids: uniqueData.map((item: any) => String(item.post_id)), // All unique post IDs after deduplication
    };
  }, [
    daysNumber,
    user_id,
    username,
    contentOrder,
    contentPlatforms,
    customOrderPostIds,
    processedRemovedPostIds,
    POSTS_PER_PAGE,
  ]);

  // Serialize removedPostIds for query key to ensure React Query detects changes
  const removedPostIdsKey = useMemo(() => {
    if (!removedPostIds || removedPostIds === "") {
      return "";
    }
    return removedPostIds
      .split(",")
      .map((id: any) => String(id).trim())
      .filter((id: string) => id !== "")
      .sort() // Sort to ensure consistent key regardless of order
      .join(",");
  }, [removedPostIds]);

  const queryKey = useMemo(
    () => [
      "YesterdayContent",
      queryScope,
      user_id,
      daysNumber,
      removedPostIdsKey, // Use serialized string instead of array for reliable change detection
      name,
      isChange,
      contentOrder,
      (customOrderPostIds || []).join("|"),
    ],
    [
      queryScope,
      user_id,
      daysNumber,
      removedPostIdsKey,
      name,
      isChange,
      contentOrder,
      customOrderPostIds,
    ]
  );

  const queryResult = useInfiniteQuery(
    queryKey,
    getPaginatedFeed,
    {
      enabled: Boolean(processedRemovedPostIds?.length >= 0) && Boolean(daysNumber),
      getNextPageParam: (lastPage: any, allPages: any) => {
        // Check if there are more pages based on original data length
        if (!lastPage || !lastPage.data) {
          return undefined;
        }

        // If original data length is less than POSTS_PER_PAGE, we've reached the end
        if (lastPage.originalDataLength !== undefined) {
          if (lastPage.originalDataLength < POSTS_PER_PAGE) {
            return undefined;
          }
        } else {
          // Fallback: check filtered data length
          if (lastPage.data.length < POSTS_PER_PAGE) {
            return undefined;
          }
        }

        // Check total loaded items against total_count
        const totalLoaded = allPages.reduce((sum: number, page: any) => {
          const pageDataLength =
            page?.originalDataLength !== undefined
              ? page.originalDataLength
              : page?.data?.length || 0;
          return sum + pageDataLength;
        }, 0);

        // If we've loaded all items based on total_count, stop
        if (lastPage.total_count && totalLoaded >= lastPage.total_count) {
          return undefined;

        }

        // Otherwise, return the next page number
        return allPages.length + 1;
      },
      keepPreviousData: true,
      ...reactQueryParams(),
    }
  );

  // Transform the result to maintain backward compatibility
  const firstPage = queryResult.data?.pages?.[0];
  
  // Get total_count from first page (which is calculated after deduplication in getPaginatedFeed)
  // This is the correct total count after combining all date ranges and deduplicating
  const totalCount =
    firstPage && typeof firstPage === "object" && "total_count" in firstPage
      ? firstPage.total_count
      : 0;

  // Get post_ids from first page (contains all unique post IDs after deduplication)
  const allPostIdsFromRPC = 
    firstPage && typeof firstPage === "object" && "post_ids" in firstPage
      ? firstPage.post_ids || []
      : [];

  // Flatten pages into single array for backward compatibility
  const flattenedData = queryResult.data?.pages?.flatMap((page: any) => page.data || []) || [];

  return {
    ...queryResult,
    data: flattenedData, // Flattened array for backward compatibility
    length: flattenedData.length, // Length of the data array
    post_ids: allPostIdsFromRPC, // All unique post IDs after deduplication across all date ranges
    dataPages: queryResult.data?.pages || [], // Keep pages structure
    total_count: totalCount, // Total count after deduplication across all date ranges (from getPaginatedFeed)
    // Do not OR isFetching: refetches would keep loaders stuck on skeleton
    // (same pattern as useLoadYesterdayLinks).
    isLoading: queryResult.isLoading,
    refetch: queryResult.refetch,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
  };
}
